import React from 'react'
import { List, useTable, ShowButton, EditButton, DeleteButton, DateField, getDefaultSortOrder, FilterDropdown } from '@refinedev/antd'
import { useList, getDefaultFilter } from '@refinedev/core'
import { Table, Space, Select } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { renderAvatarName } from '../../theme/columns'
import { useRowShow, ListPage, Pill, TableSurface, type Stat, type Tone } from '../../theme/listkit'
import { HandHeart, UsersRound, CalendarCheck } from '../../components/icons'

// Embedded profile shape (name + class_year) from the FK joins.
type RelProfile = Pick<Tables<'profiles'>, 'name' | 'class_year'>
type Relationship = Tables<'mentorship_relationships'> & {
  mentor: RelProfile | null
  mentee: RelProfile | null
}

// The FK embeds: mentor/mentee resolved through the named profile foreign keys.
const SELECT =
  '*, mentor:profiles!mentorship_relationships_mentor_profile_fkey(name,class_year), mentee:profiles!mentorship_relationships_mentee_profile_fkey(name,class_year)'

/** Status → pill tone. Lifecycle: active/paused/completed/ended. */
const STATUS_TONE: Record<string, Tone> = {
  active: 'green',
  paused: 'gold',
  completed: 'jaguar',
  ended: 'red',
}

export function MentorshipRelationshipList() {
  const { tableProps, sorters, filters } = useTable<Relationship>({
    // EXPLICIT resource: this list now renders INSIDE the Mentorship hub route
    // (/mentorship_requests, as the "Relationships" tab), so without this the
    // table would infer the route's resource (mentorship_requests) and run the
    // relationship FK-embed select against the wrong table → empty table. The
    // summary-deck counts already pass resource explicitly, which is why they
    // showed "2 active" while the table stayed empty.
    resource: 'mentorship_relationships',
    // syncWithLocation off: as a tab it shares the hub URL with the Requests
    // tab's table; URL-syncing both would collide on the same query params.
    syncWithLocation: false,
    sorters: { initial: [{ field: 'started_at', order: 'desc' }] },
    meta: { select: SELECT },
  })
  const onRow = useRowShow('mentorship_relationships')

  // Summary deck — bounded count queries (pageSize:1 → PostgREST returns the
  // total without the rows). Replaces the old unbounded useList over the whole
  // `mentorship_relationships` table. One tiny status=eq count per pill; they
  // run in parallel.
  const { result: activeRes } = useList<Relationship>({
    resource: 'mentorship_relationships',
    filters: [{ field: 'status', operator: 'eq', value: 'active' }],
    pagination: { pageSize: 1 },
  })
  const { result: pausedRes } = useList<Relationship>({
    resource: 'mentorship_relationships',
    filters: [{ field: 'status', operator: 'eq', value: 'paused' }],
    pagination: { pageSize: 1 },
  })
  const { result: completedRes } = useList<Relationship>({
    resource: 'mentorship_relationships',
    filters: [{ field: 'status', operator: 'eq', value: 'completed' }],
    pagination: { pageSize: 1 },
  })
  const active = activeRes?.total ?? 0
  const paused = pausedRes?.total ?? 0
  const completed = completedRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Active', value: active.toLocaleString(), hint: 'in progress', icon: HandHeart, tone: 'green' },
    { label: 'Paused', value: paused.toLocaleString(), icon: UsersRound, tone: 'gold' },
    { label: 'Completed', value: completed.toLocaleString(), icon: CalendarCheck, tone: 'jaguar' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Community · Mentorship',
        description:
          'Live mentor↔mentee pairings — the real relationships that form once a request is accepted. Track who is being mentored, the focus and cadence, and step in to pause or close a relationship when staff need to.',
      }}
      stats={stats}
      statColumns={3}
    >
      <List resource="mentorship_relationships" canCreate={false} title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Relationship>
              dataIndex="mentor_id"
              title="Mentor"
              render={(_: string, record: Relationship) => (
                <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
                  {renderAvatarName(record.mentor?.name ?? record.mentor_id)}
                  {record.mentor?.class_year ? (
                    <span style={{ fontFamily: brand.fSans, fontSize: 11, color: brand.sub, paddingLeft: 40 }}>
                      Class of {record.mentor.class_year}
                    </span>
                  ) : null}
                </span>
              )}
            />
            <Table.Column<Relationship>
              dataIndex="mentee_id"
              title="Mentee"
              render={(_: string, record: Relationship) =>
                renderAvatarName(record.mentee?.name ?? record.mentee_id)
              }
            />
            <Table.Column<Relationship>
              dataIndex="status"
              title="Status"
              defaultFilteredValue={getDefaultFilter('status', filters, 'eq')}
              filterDropdown={(props) => (
                <FilterDropdown {...props}>
                  <Select
                    style={{ minWidth: 160 }}
                    placeholder="Filter by status"
                    allowClear
                    options={[
                      { value: 'active', label: 'Active' },
                      { value: 'paused', label: 'Paused' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'ended', label: 'Ended' },
                    ]}
                  />
                </FilterDropdown>
              )}
              render={(status: string) => <Pill tone={STATUS_TONE[status] ?? 'neutral'}>{status}</Pill>}
            />
            <Table.Column<Relationship>
              dataIndex="kind"
              title="Kind"
              render={(kind: string) => (
                <span style={{ fontFamily: brand.fSans, fontWeight: 500, color: brand.ink }}>
                  {kind === 'one_on_one' ? 'One-on-one' : kind === 'circle' ? 'Circle' : kind}
                </span>
              )}
            />
            <Table.Column<Relationship>
              dataIndex="started_at"
              title="Started"
              sorter
              defaultSortOrder={getDefaultSortOrder('started_at', sorters)}
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Relationship>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Relationship) => (
                <Space>
                  <ShowButton resource="mentorship_relationships" hideText size="small" recordItemId={record.id} />
                  <EditButton resource="mentorship_relationships" hideText size="small" recordItemId={record.id} />
                  <DeleteButton resource="mentorship_relationships" hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
